// src/i18n/index.js
import { createContext, useContext, useState, useEffect } from 'react';
import STRINGS from './strings';
import { nombrePais } from '../data/paisesDB';

export const LANGS = [
  { code: 'es',    name: 'Español',    flag: 'ES' },
  { code: 'en',    name: 'English',    flag: 'EN' },
  { code: 'fr',    name: 'Français',   flag: 'FR' },
  { code: 'de',    name: 'Deutsch',    flag: 'DE' },
  { code: 'it',    name: 'Italiano',   flag: 'IT' },
  { code: 'nl',    name: 'Nederlands', flag: 'NL' },
  { code: 'pt-BR', name: 'Português',  flag: 'PT' },
];

const SUPPORTED = LANGS.map(l => l.code);

// Sustituye {0}, {1}… en un texto traducido: fmt(t('clave'), valor)
export const fmt = (s, ...a) => String(s).replace(/\{(\d+)\}/g, (m, i) => (a[i] ?? ''));
const KEY = 'acescn_lang';

export function detect() {
  // Prioridad 1: idioma guardado explícitamente por el usuario
  const saved = localStorage.getItem(KEY);
  if (saved && SUPPORTED.includes(saved)) return saved;
  // Prioridad 2: idioma del navegador si es uno de los 7 (pt-* → pt-BR)
  const nav = (typeof navigator !== 'undefined' && (navigator.languages?.[0] || navigator.language)) || '';
  const base = nav.toLowerCase().split('-')[0];
  if (base === 'pt') return 'pt-BR';
  if (SUPPORTED.includes(base)) return base;
  // Prioridad 3: idioma no soportado (búlgaro, eslovaco...) → inglés, igual que iOS y Android
  return 'en';
}

const Ctx = createContext(null);
export const useLang = () => useContext(Ctx);

// Idioma activo para helpers fuera de hooks (se actualiza en cada render del provider,
// que ocurre antes que el de sus consumidores).
let _langActual = 'en';
/** País guardado (nombre en español/otro idioma o ISO) → nombre en el idioma activo. */
export const paisUI = guardado => nombrePais(guardado, _langActual);

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(detect);
  _langActual = lang;

  const setLang = code => {
    if (!SUPPORTED.includes(code)) return;
    localStorage.setItem(KEY, code);
    setLangState(code);
    document.documentElement.lang = code;
  };

  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  // Escuchar cambios de idioma desde AuthContext (sincronización Firestore)
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'acescn_lang' && e.newValue && SUPPORTED.includes(e.newValue)) {
        setLangState(e.newValue);
        document.documentElement.lang = e.newValue;
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Clave ausente en el idioma: inglés y, en último caso, español (paridad iOS/Android)
  const t = key => STRINGS[lang]?.[key] ?? STRINGS['en']?.[key] ?? STRINGS['es']?.[key] ?? key;

  return <Ctx.Provider value={{ lang, setLang, t, LANGS }}>{children}</Ctx.Provider>;
}
